import { useParams, Navigate, Link } from "react-router-dom";
import { CheckCircle2, Sparkles, Star, ArrowRight, GraduationCap, Phone, MessageCircle } from "lucide-react";
import PageHeader from "../components/PageHeader";
import SEO from "../components/SEO";
import Breadcrumbs from "../components/Breadcrumbs";
import RegistrationForm from "../components/RegistrationForm";
import WhyChooseAnandTutorials from "../components/WhyChooseAnandTutorials";
import { QuickAnswerCard } from "../components/AEOContent";
import PillarComparison from "../components/PillarComparison";
import { BOARD_COURSES } from "../data/boardCoursesData";
import { getCoursePillarQuickAnswer } from "../data/aeoContent";
import { getPillarComparison } from "../data/pillarComparisonData";
import { useSettings } from "../context/SettingsContext";

/**
 * Programmatic SEO micro-landing page for each board / exam.
 * Route: /courses/:boardSlug — driven entirely by BOARD_COURSES data.
 */
export default function BoardCoursePage() {
    const { boardSlug } = useParams();
    const board = BOARD_COURSES[boardSlug];
    const { settings } = useSettings();
    if (!board) return <Navigate to="/courses" replace />;

    const phone = settings.head_office_phone || "+91 7000530316";
    const whatsapp = settings.whatsapp_number || "917000530316";

    const canonicalPath = `/courses/${board.slug}`;
    const canonicalUrl = `https://anandtutorials.com${canonicalPath}`;

    // 1) Course schema with full metadata
    const courseSchema = {
        "@context": "https://schema.org",
        "@type": "Course",
        "@id": `${canonicalUrl}#course`,
        "name": board.label,
        "description": board.intro,
        "provider": { "@id": "https://anandtutorials.com#organization" },
        "url": canonicalUrl,
        "educationalLevel": board.educationalLevel || "K-12 / Senior Secondary / Competitive",
        "inLanguage": "en-IN",
        "hasCourseInstance": {
            "@type": "CourseInstance",
            "courseMode": ["onsite", "online"],
            "courseWorkload": "PT3H",
            "instructor": { "@type": "EducationalOrganization", "name": "Anand Tutorials" },
            "location": [
                { "@type": "Place", "name": "Jabalpur, Madhya Pradesh" },
                { "@type": "Place", "name": "Bhopal, Madhya Pradesh" },
                { "@type": "Place", "name": "Nagpur, Maharashtra" },
                { "@type": "VirtualLocation", "name": "1:1 Live Online (Pan-India + 27 Countries)" },
            ],
        },
        "offers": {
            "@type": "Offer",
            "category": "Paid",
            "priceCurrency": "INR",
            "availability": "https://schema.org/InStock",
            "url": "https://anandtutorials.com/register",
        },
    };

    // 2) Organisation (brand root)
    const organizationSchema = {
        "@context": "https://schema.org",
        "@type": "EducationalOrganization",
        "@id": "https://anandtutorials.com#organization",
        "name": "Anand Tutorials",
        "url": "https://anandtutorials.com",
        "logo": "https://anandtutorials.com/logo-anand-tutorials.png",
        "foundingDate": "2013",
    };

    // 3) WebPage
    const webPageSchema = {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "@id": `${canonicalUrl}#webpage`,
        "url": canonicalUrl,
        "name": board.seoTitle,
        "description": board.seoDescription,
        "inLanguage": "en-IN",
        "isPartOf": { "@id": "https://anandtutorials.com#organization" },
        "about": { "@id": `${canonicalUrl}#course` },
        "breadcrumb": { "@id": `${canonicalUrl}#breadcrumb` },
    };

    // 4) BreadcrumbList — Home › Courses › <Board>
    const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "@id": `${canonicalUrl}#breadcrumb`,
        "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://anandtutorials.com/" },
            { "@type": "ListItem", "position": 2, "name": "Courses", "item": "https://anandtutorials.com/courses" },
            { "@type": "ListItem", "position": 3, "name": board.label, "item": canonicalUrl },
        ],
    };

    // 5) FAQPage
    const faqSchema = board.faqs && board.faqs.length ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "@id": `${canonicalUrl}#faq`,
        "mainEntity": board.faqs.map(([q, a]) => ({
            "@type": "Question",
            "name": q,
            "acceptedAnswer": { "@type": "Answer", "text": a },
        })),
    } : null;

    // 6) Batch 2P — Course-pillar QuickAnswer (AEO / AI Overview lift target).
    // The FAQPage schema above already carries the 8-item Q&A pack from
    // Batch 2H, so this block emits only presentation + Speakable schema —
    // no duplicate FAQPage entities.
    const pillarQA = getCoursePillarQuickAnswer(board.slug);
    const comparisonGroup = getPillarComparison(board.slug);
    const speakableSchema = pillarQA ? {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "@id": `${canonicalUrl}#speakable`,
        "url": canonicalUrl,
        "speakable": {
            "@type": "SpeakableSpecification",
            "cssSelector": ["#quick-answer-heading", "[data-speakable='true']"],
        },
    } : null;

    return (
        <div data-testid={`board-course-${board.slug}`} className="bg-white">
            <SEO
                title={board.seoTitle}
                description={board.seoDescription}
                keywords={board.keywords}
                path={canonicalPath}
                canonicalPath={canonicalPath}
                schemas={[
                    courseSchema,
                    organizationSchema,
                    webPageSchema,
                    breadcrumbSchema,
                    faqSchema,
                    speakableSchema,
                ]}
            />

            <Breadcrumbs items={[
                { name: "Courses", to: "/courses" },
                { name: board.label },
            ]} />

            <PageHeader
                eyebrow={board.eyebrow}
                title={board.h1}
                subtitle={board.intro}
            >
                <div className="flex flex-wrap gap-3">
                    <Link to="/register" data-testid={`board-${board.slug}-cta-demo`} className="inline-flex items-center gap-2 bg-amber-500 text-[#0F172A] font-semibold px-5 py-3 rounded-lg hover:bg-amber-400 transition-colors">
                        Book Free Demo <ArrowRight className="w-4 h-4" />
                    </Link>
                    <a href={`tel:${phone.replace(/\s/g, "")}`} data-testid={`board-${board.slug}-cta-call`} className="inline-flex items-center gap-2 bg-white/10 border border-white/20 backdrop-blur text-white font-semibold px-5 py-3 rounded-lg hover:bg-white/20 transition-colors">
                        <Phone className="w-4 h-4" /> Talk to Us
                    </a>
                </div>
            </PageHeader>

            {/* Batch 2P — Course-Pillar Quick-Answer card (AEO / AI Overview lift target) */}
            {pillarQA && (
                <QuickAnswerCard
                    question={pillarQA.question}
                    answer={pillarQA.answer}
                    bullets={pillarQA.bullets}
                    footer={pillarQA.footer}
                    testId={`course-quick-answer-${board.slug}`}
                />
            )}

            {/* What's covered */}
            <section className="py-16 sm:py-20 bg-white">
                <div className="max-w-5xl mx-auto px-6 lg:px-8">
                    <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#3B82F6] mb-3">What's covered</div>
                    <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#0F172A] mb-8">Full syllabus. Nothing left out.</h2>
                    <div className="grid sm:grid-cols-2 gap-3">
                        {board.highlights.map((h) => (
                            <div key={h} className="flex items-start gap-3 p-4 rounded-xl bg-[#F8FAFC] border border-slate-200">
                                <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                                <div className="text-sm text-[#0F172A] leading-relaxed">{h}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Learning outcomes — Batch 2H addition */}
            {board.learningOutcomes && board.learningOutcomes.length > 0 && (
                <section className="py-16 sm:py-20 bg-white border-t border-slate-100" data-testid={`board-${board.slug}-outcomes`}>
                    <div className="max-w-5xl mx-auto px-6 lg:px-8">
                        <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-emerald-600 mb-3">Learning outcomes</div>
                        <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#0F172A] mb-3">What your child will be able to do after the plan.</h2>
                        <p className="text-slate-600 mb-8 max-w-3xl">Concrete, measurable, exam-anchored outcomes — no vague promises. Every outcome ties to a specific mark-scheme, band, or exam-day capability.</p>
                        <ol className="grid sm:grid-cols-2 gap-3">
                            {board.learningOutcomes.map((o, i) => (
                                <li key={i} className="flex items-start gap-3 p-4 rounded-xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100" data-testid={`board-${board.slug}-outcome-${i}`}>
                                    <div className="w-6 h-6 rounded-full bg-emerald-600 text-white flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">{i + 1}</div>
                                    <div className="text-sm text-[#0F172A] leading-relaxed">{o}</div>
                                </li>
                            ))}
                        </ol>
                    </div>
                </section>
            )}

            {/* Mid-page CTA band — Batch 2H addition */}
            {board.midCta && (
                <section className="py-12 bg-gradient-to-br from-[#0F172A] to-[#1E3A8A] text-white" data-testid={`board-${board.slug}-mid-cta`}>
                    <div className="max-w-5xl mx-auto px-6 lg:px-8 flex flex-col md:flex-row md:items-center justify-between gap-6">
                        <div className="max-w-2xl">
                            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-amber-300 mb-2">Free · 45 min · no card</div>
                            <h3 className="font-heading text-xl sm:text-2xl font-semibold mb-2">{board.midCta.headline}</h3>
                            <p className="text-blue-100 text-sm leading-relaxed">{board.midCta.sub}</p>
                        </div>
                        <div className="flex flex-wrap gap-3 shrink-0">
                            <Link
                                to="/register"
                                data-testid={`board-${board.slug}-mid-cta-demo`}
                                className="inline-flex items-center gap-2 bg-amber-500 text-[#0F172A] font-semibold px-5 py-3 rounded-lg hover:bg-amber-400 transition-colors"
                            >
                                Book Free Demo <ArrowRight className="w-4 h-4" />
                            </Link>
                            <a
                                href={`https://wa.me/${whatsapp}?text=${encodeURIComponent(`Hi — I'd like a free demo for ${board.label}`)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`board-${board.slug}-mid-cta-whatsapp`}
                                className="inline-flex items-center gap-2 bg-[#22C55E] hover:bg-[#16A34A] text-white font-semibold px-5 py-3 rounded-lg"
                            >
                                <MessageCircle className="w-4 h-4" /> WhatsApp
                            </a>
                        </div>
                    </div>
                </section>
            )}

            {/* Why Anand for this board */}
            <section className="py-16 sm:py-20 bg-[#F8FAFC] border-y border-slate-100">
                <div className="max-w-5xl mx-auto px-6 lg:px-8">
                    <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-amber-600 mb-3 flex items-center gap-2">
                        <Sparkles className="w-3 h-3" /> Why Anand Tutorials
                    </div>
                    <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#0F172A] mb-8">
                        Tutors who've actually taught (and aced) {board.label}.
                    </h2>
                    <div className="grid sm:grid-cols-2 gap-4">
                        {board.whyUs.map((w) => (
                            <div key={w} className="bg-white p-5 rounded-2xl border border-slate-200">
                                <div className="flex items-start gap-3">
                                    <div className="w-8 h-8 rounded-lg bg-[#1E3A8A]/5 text-[#1E3A8A] flex items-center justify-center shrink-0">
                                        <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                                    </div>
                                    <p className="text-sm text-slate-700 leading-relaxed">{w}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* WHY CHOOSE — EEAT block */}
            <WhyChooseAnandTutorials
                eyebrow={`Why ${board.label} parents trust us`}
                headline={`Why Anand Tutorials is India's most trusted ${board.label} 1:1 brand.`}
                contextLabel={`for ${board.label} home tuition and live online classes pan-India`}
            />

            {/* Batch 2Q — Pillar Comparison Widget (head-to-head "X vs Y" AEO) */}
            {comparisonGroup && (
                <PillarComparison currentSlug={board.slug} group={comparisonGroup} />
            )}

            {/* Related cities — Batch 2I: course ↔ city topic-cluster cross-links */}
            {board.relatedCities && board.relatedCities.length > 0 && (
                <section className="py-16 sm:py-20 bg-white border-t border-slate-100" data-testid={`board-${board.slug}-related-cities`}>
                    <div className="max-w-7xl mx-auto px-6 lg:px-8">
                        <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#3B82F6] mb-3">Where our {board.label} students study from</div>
                        <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#0F172A] mb-3">Same {board.label} mentor, wherever you live.</h2>
                        <p className="text-slate-600 mb-8 max-w-3xl">
                            Our 1:1 live online {board.label} classes reach students across India and 30+ countries. Every mentor is matched to your board / exam-code — click your city or curriculum region below to see the local school ecosystem, syllabus notes and demo-booking flow.
                        </p>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {board.relatedCities.map((rc, i) => (
                                <Link
                                    key={rc.path}
                                    to={rc.path}
                                    data-testid={`board-${board.slug}-related-${i}`}
                                    className="group flex items-start gap-3 p-4 rounded-xl bg-[#F8FAFC] border border-slate-200 hover:border-[#1E3A8A] hover:bg-white hover:shadow-sm transition-all"
                                >
                                    <div className="w-9 h-9 rounded-lg bg-[#1E3A8A]/5 text-[#1E3A8A] flex items-center justify-center shrink-0 group-hover:bg-[#1E3A8A] group-hover:text-white transition-colors">
                                        <ArrowRight className="w-4 h-4" />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="font-heading font-semibold text-[#0F172A] text-sm mb-1 group-hover:text-[#1E3A8A]">
                                            {board.label} tutors in {rc.label}
                                        </div>
                                        <div className="text-xs text-slate-600 leading-relaxed">{rc.tagline}</div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* FAQ + Registration form */}
            <section className="py-16 sm:py-20 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-5 gap-10 lg:gap-14">
                    <div className="lg:col-span-3">
                        <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#3B82F6] mb-3">Common questions</div>
                        <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#0F172A] mb-6">Parents ask about {board.label} — here's what we tell them.</h2>
                        <div className="space-y-3">
                            {board.faqs.map(([q, a], i) => (
                                <details key={q} data-testid={`board-${board.slug}-faq-${i}`} className="group bg-[#F8FAFC] border border-slate-200 rounded-xl p-5 [&_summary::-webkit-details-marker]:hidden">
                                    <summary className="cursor-pointer font-heading font-semibold text-[#0F172A] flex items-center justify-between gap-3">
                                        {q}
                                        <span className="text-[#1E3A8A] text-lg group-open:rotate-45 transition-transform">+</span>
                                    </summary>
                                    <div className="text-sm text-slate-600 mt-3 leading-relaxed">{a}</div>
                                </details>
                            ))}
                        </div>

                        <div className="mt-10 p-5 rounded-2xl bg-gradient-to-br from-[#0F172A] to-[#1E3A8A] text-white flex flex-wrap items-center justify-between gap-4">
                            <div>
                                <div className="text-[10px] font-bold uppercase tracking-wider text-amber-300 mb-1">Still not sure?</div>
                                <div className="font-heading font-semibold">Grab a 100% free demo class for {board.label}.</div>
                            </div>
                            <a
                                href={`https://wa.me/${whatsapp}?text=${encodeURIComponent(`Hi — I'd like a free demo for ${board.label}`)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`board-${board.slug}-whatsapp`}
                                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#22C55E] hover:bg-[#16A34A] text-white text-sm font-semibold"
                            >
                                <MessageCircle className="w-4 h-4" /> WhatsApp
                            </a>
                        </div>
                    </div>
                    <div className="lg:col-span-2">
                        <div className="sticky top-24">
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-50 border border-amber-200 text-[10px] uppercase tracking-[0.22em] font-bold text-amber-700 mb-3">
                                <GraduationCap className="w-3 h-3" /> Quick booking
                            </div>
                            <h3 className="font-heading text-xl font-bold text-[#0F172A] mb-4">Book your free {board.label} demo</h3>
                            <RegistrationForm />
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
